import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Animated, StyleSheet, View, } from 'react-native';
import { Divider, Text, TextInput, TouchableRipple, useTheme } from 'react-native-paper';
import { Easing } from 'react-native-reanimated';
import { EmptyState } from '../EmptyState/EmptyState';
export const DEFAULT_DROP_DOWN_ANIMATION_DURATION = 100;
export const DEFAULT_MAX_DROPDOWN_HEIGHT = 200;
export const DropDown = forwardRef(({ disabled = false, isOpen = false, ...props }, _ref) => {
    const theme = useTheme();
    const styles = StyleSheet.create({
        item: {
            flex: 1,
            padding: 20,
            backgroundColor: theme.colors.surface,
        },
        highlightItem: {
            flex: 1,
            backgroundColor: theme.colors.primary,
            borderColor: theme.colors.secondary,
            borderWidth: 1,
            borderStyle: 'solid',
            borderRadius: 5,
        },
        highlightText: {
            color: theme.colors.onPrimary,
        },
        itemText: {
            color: theme.colors.onSurface,
        },
    });
    const [isFocusEnabled, setIsFocusEnabled] = useState(false);
    const [displayValue, setDisplayValue] = useState('');
    const [layout, setLayout] = useState();
    const [selectedItemIndex, setSelectedItemIndex] = useState();
    const [itemHeight, setItemHeight] = useState(0);
    const [dividerHeight, setDividerHeight] = useState(0);
    const maxHeight = props.maxDropDownHeight || DEFAULT_MAX_DROPDOWN_HEIGHT;
    const dropDownAnimated = props.animated || true;
    const dropDownAnimationDuration = dropDownAnimated
        ? props.animationDuration || DEFAULT_DROP_DOWN_ANIMATION_DURATION
        : 0;
    const dropDownHeight = useState(new Animated.Value(0))[0];
    const [dropdownItemHeight, setDropDownItemHeight] = useState(0);
    const [maxDropDownHeight, setMaxDropDownHeight] = useState(maxHeight);
    const [loading, setLoading] = useState(true);
    //@ts-ignore
    const inputRef = useRef(null);
    const itemListRef = useRef(null);
    useEffect(() => {
        if (props.items && itemHeight > 0 && dividerHeight > 0) {
            const actualDropDownHeight = (itemHeight + dividerHeight) * props.items.length;
            setDropDownItemHeight(itemHeight + dividerHeight);
            setMaxDropDownHeight(maxHeight < actualDropDownHeight ? maxHeight : actualDropDownHeight);
            setLoading(false);
        }
    }, [props.items, itemHeight, dividerHeight]);
    useEffect(() => {
        isOpen ? openDropDown() : closeDropDown();
    }, [isOpen]);
    useEffect(() => {
        const toSelect = props.items?.find((item) => item.value === props.value);
        if (toSelect)
            onDropDownChange(toSelect);
    }, [props.value]);
    useEffect(() => {
        if ((props.items?.length || 0) > 1) {
            itemListRef?.current?.scrollToIndex({
                animated: true,
                index: selectedItemIndex || 1,
            });
        }
    }, [selectedItemIndex]);
    const openDropDown = () => {
        Animated.timing(dropDownHeight, {
            toValue: maxDropDownHeight,
            duration: dropDownAnimationDuration,
            easing: Easing.linear,
            useNativeDriver: false,
        }).start();
        if (isFocusEnabled) {
            inputRef.current?.focus();
        }
        else {
            setIsFocusEnabled(true);
        }
    };
    const closeDropDown = () => {
        Animated.timing(dropDownHeight, {
            toValue: 0,
            duration: dropDownAnimationDuration,
            easing: Easing.linear,
            useNativeDriver: false,
        }).start();
    };
    const onDropDownPress = () => {
        if (!disabled && isOpen && props.onDismiss) {
            props.onDismiss();
        }
        else if (!disabled && !isOpen && props.onOpen) {
            props.onOpen();
        }
    };
    const onDropDownChange = (item) => {
        if (item) {
            setDisplayValue(item?.label);
            setSelectedItemIndex(props?.items?.indexOf(item));
        }
        else {
            setDisplayValue('');
            setSelectedItemIndex(undefined);
        }
        props.onChange?.(item);
        props.onDismiss?.();
    };
    const onDropDownItemPress = (item) => {
        onDropDownChange(item);
    };
    const onItemLayout = (event) => {
        setItemHeight(event.nativeEvent.layout.height);
    };
    const renderDropDownItem = (item) => (_jsx(TouchableRipple, { testID: `${props.testID}-dropdown-item-${item.index}`, onPress: () => onDropDownItemPress(item.item), onLayout: onItemLayout, children: _jsx(View, { testID: `${props.testID}-dropdown-item-view-${item.index}`, style: [styles.item, selectedItemIndex === item.index ? styles.highlightItem : {}], children: _jsx(Text, { testID: `${props.testID}-dropdown-item-text-${item.index}`, style: [selectedItemIndex === item.index ? styles.highlightText : styles.itemText], children: item.item.label ? item.item.label : props.emptyMessage }) }) }));
    const onLayout = (event) => {
        setLayout(event.nativeEvent.layout);
    };
    const onChangeText = (value) => {
        if (!isOpen && props.onOpen && value) {
            props.onOpen();
        }
        if (value) {
            const matchedItem = props.items?.find((item) => item.label.toLowerCase().startsWith(value.toLowerCase()));
            if (matchedItem) {
                const matchedItemIndex = props.items.indexOf(matchedItem);
                setSelectedItemIndex(matchedItemIndex);
                setDisplayValue(value);
            }
        }
        else {
            onDropDownChange(undefined);
        }
    };
    const getDefaultItemLayout = (data, index) => ({
        length: itemHeight,
        offset: dropdownItemHeight * index,
        index,
    });
    const onDividerLayout = (event) => {
        if (!dividerHeight) {
            setDividerHeight(event.nativeEvent.layout.height);
        }
    };
    const renderSeparator = () => _jsx(Divider, { onLayout: onDividerLayout });
    const onKeyPress = (event) => {
        if (event.nativeEvent.key === 'Escape' && props.onDismiss) {
            props.onDismiss();
        }
        if (event.nativeEvent.key === 'Enter') {
            const selected = props.items?.find((item, index) => index === selectedItemIndex);
            if (selected) {
                onDropDownChange(selected);
            }
        }
        if (event.nativeEvent.key === 'ArrowDown') {
            if (isOpen) {
                if (selectedItemIndex === undefined && props.items?.length) {
                    setSelectedItemIndex(0);
                }
                else {
                    const newSelectionIndex = (selectedItemIndex ?? -1) + 1;
                    if (newSelectionIndex < (props.items?.length || 0)) {
                        setSelectedItemIndex(newSelectionIndex);
                    }
                }
            }
            else {
                onDropDownPress();
            }
        }
        if (event.nativeEvent.key === 'ArrowUp') {
            const newSelectionIndex = (selectedItemIndex || 0) - 1;
            setSelectedItemIndex(newSelectionIndex > -1 ? newSelectionIndex : selectedItemIndex);
        }
    };
    return (_jsxs(View, { style: [{ zIndex: 1, margin: 3 }, props.style], testID: `${props.testID}`, onLayout: onLayout, children: [_jsx(TextInput, { ref: inputRef, style: props.style, disabled: disabled, testID: `${props.testID}-input`, mode: props.mode || 'outlined', label: props.label, placeholder: props.placeholder, value: displayValue, onPointerUp: onDropDownPress, onChangeText: onChangeText, onBlur: props.onBlur, onKeyPress: onKeyPress, error: props.error, right: _jsx(TextInput.Icon, { testID: `${props.testID}-text-input-icon`, disabled: disabled, onPress: onDropDownPress, icon: () => (_jsx(MaterialCommunityIcons, { testID: `${props.testID}-icon`, name: isOpen ? 'menu-up' : 'menu-down', size: 24, color: theme.colors.onSurface })) }) }), (loading || isOpen) && (_jsx(Animated.FlatList, { testID: `${props.testID}-flat-list`, ref: itemListRef, data: props.items, initialScrollIndex: selectedItemIndex, ItemSeparatorComponent: renderSeparator, ListEmptyComponent: EmptyState({ text: props.emptyMessage || 'No items found' }), renderItem: renderDropDownItem, getItemLayout: getDefaultItemLayout, style: [
                    {
                        flex: 1,
                        shadowColor: theme.colors.shadow,
                        shadowRadius: 2,
                        borderColor: props.error ? theme.colors.error : theme.colors.primary,
                        borderWidth: isOpen ? 2 : 0,
                        borderTopWidth: 0,
                        borderBottomLeftRadius: 5,
                        borderBottomRightRadius: 5,
                        backgroundColor: theme.colors.surfaceVariant,
                        maxHeight: maxHeight,
                        height: dropDownHeight.interpolate({
                            inputRange: [0, maxDropDownHeight],
                            outputRange: [0, maxDropDownHeight],
                        }),
                        position: 'absolute',
                        top: layout?.height || 0,
                        width: layout?.width,
                        zIndex: 2,
                    },
                    props.dropdownStyle,
                    props.position === 'top' ? { top: 'auto', bottom: '100%' } : {},
                ] }))] }));
});
